import { Badge, Box, Card, Group, Stack, Text, Title } from "@mantine/core";
import { Markdown } from "./Markdown";

export type ChangelogKind = "added" | "improved" | "fixed";
export type ChangelogSurface = "admin" | "portal" | "launcher";

export interface ChangelogEntry {
  kind: ChangelogKind;
  surface: ChangelogSurface;
  markdown: string;
}

export interface ChangelogVersion {
  version: string;
  /** ISO date from the release file; may be null even for released versions. */
  released_at: string | null;
  /** Effective release state for the serving environment (false = upcoming). */
  released: boolean;
  summary: string;
  entries: ChangelogEntry[];
}

export interface ChangelogListProps {
  versions: ChangelogVersion[];
  /**
   * Show a per-entry badge for which surface the change concerns
   * (Admin / Portal / Launcher). The admin frontend turns this on — it
   * receives entries for every surface; the member portal gets portal-only
   * entries and leaves it off.
   */
  showSurface?: boolean;
}

const KIND_COLOR: Record<ChangelogKind, string> = {
  added: "green",
  improved: "blue",
  fixed: "orange",
};

const SURFACE_LABEL: Record<ChangelogSurface, string> = {
  admin: "Admin",
  portal: "Portal",
  launcher: "Launcher",
};

/** Release-notes list shared by the management frontend and the member portal. */
export function ChangelogList({ versions, showSurface = false }: ChangelogListProps) {
  if (versions.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        No release notes yet.
      </Text>
    );
  }
  return (
    <Stack gap="lg">
      {versions.map((version) => (
        <Card key={version.version} withBorder padding="lg">
          <Group gap="sm" align="baseline" mb={version.summary ? 4 : "md"}>
            <Title order={3}>v{version.version}</Title>
            {version.released ? (
              version.released_at && (
                <Text c="dimmed" fz="sm">
                  {version.released_at}
                </Text>
              )
            ) : (
              <Badge color="grape" variant="light">
                Upcoming
              </Badge>
            )}
          </Group>
          {version.summary && (
            <Text c="dimmed" fz="sm" mb="md">
              {version.summary}
            </Text>
          )}
          <Stack gap="sm">
            {version.entries.map((entry, index) => (
              <Group key={index} gap="sm" align="flex-start" wrap="nowrap">
                <Group gap={4} wrap="nowrap" mt={2}>
                  <Badge color={KIND_COLOR[entry.kind]} variant="light" size="sm" miw={76} ta="center">
                    {entry.kind}
                  </Badge>
                  {showSurface && (
                    <Badge color="gray" variant="outline" size="sm">
                      {SURFACE_LABEL[entry.surface] ?? entry.surface}
                    </Badge>
                  )}
                </Group>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Markdown size="sm" compact>
                    {entry.markdown}
                  </Markdown>
                </Box>
              </Group>
            ))}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
